/**
 * CompatiblePatientsDrawer
 *
 * Two modes:
 * - Default mode (slot._isPatientView === false): shows compatible patients for a vacant slot.
 * - Patient view mode (slot._isPatientView === true): shows suggested slots for a patient,
 *   with a "Best Opportunity" featured card at the top.
 */
import React from 'react';
import { Drawer, IconButton, Button, FormControl, Select, MenuItem } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ScheduleIcon from '@mui/icons-material/Schedule';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import BestOpportunityCard from './BestOpportunityCard';
import { getCompatibilityLabel } from '../utils/relevanceEngine';
import styles from '../WaitingListV2Page.module.css';
import { PRIORITY_LABELS } from '../constants/waitingListConstants';

const MONTHS_PT = [
  'janeiro', 'fevereiro', 'março', 'abril', 'maio', 'junho',
  'julho', 'agosto', 'setembro', 'outubro', 'novembro', 'dezembro',
];
const WEEKDAYS_PT = [
  'domingo', 'segunda-feira', 'terça-feira', 'quarta-feira',
  'quinta-feira', 'sexta-feira', 'sábado',
];

function parseSlotDateTime(date, time) {
  if (!date) return null;
  try {
    const d = new Date(`${date}T12:00:00`);
    if (isNaN(d.getTime())) return null;
    return {
      weekday: WEEKDAYS_PT[d.getDay()],
      day: d.getDate(),
      month: MONTHS_PT[d.getMonth()],
      year: d.getFullYear(),
      time: time || null,
    };
  } catch {
    return null;
  }
}

function CompatiblePatientsDrawer({
  open,
  onClose,
  slot,
  compatiblePatients = [],
  sortBy = 'compatibility',
  onSortChange,
  onOfferSlot,
  onSchedule,
  // Offer state for best opportunity card
  offeredSlotKey = null,       // key of the currently offered slot (e.g. "2026-07-06_08:00")
  onOfferAccepted,
  onOfferDenied,
  onOfferResend,
}) {
  if (!slot) return null;

  const getRankClass = (index) => {
    if (index === 0) return styles.drawerPatientRank1;
    if (index === 1) return styles.drawerPatientRank2;
    if (index === 2) return styles.drawerPatientRank3;
    return styles.drawerPatientRankDefault;
  };

  const getPriorityClass = (priority) => {
    if (priority === 'HIGH') return styles.priorityHigh;
    if (priority === 'MEDIUM') return styles.priorityMedium;
    return styles.priorityLow;
  };

  const isPatientView = slot._isPatientView === true;

  // In patient view: first item is the best opportunity, rest are "other options"
  const bestSlot = isPatientView ? compatiblePatients[0] : null;
  const otherSlots = isPatientView ? compatiblePatients.slice(1, 10) : compatiblePatients.slice(0, 10);
  const totalCount = compatiblePatients.length;

  const bestSlotKey = bestSlot ? `${bestSlot.slotDate}_${bestSlot.slotTime}` : null;
  const isBestOffered = bestSlotKey && offeredSlotKey === bestSlotKey;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: { xs: '100%', sm: 420 }, boxShadow: '-4px 0 24px rgba(0,0,0,0.08)' } }}
    >
      <div className={styles.drawerContainer}>
        {/* Header */}
        <div className={styles.drawerHeader}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div className={styles.drawerSlotInfo}>
              {isPatientView ? (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                    <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>Horários sugeridos</span>
                  </div>
                  <span className={styles.drawerSlotTime}>{slot._patientName}</span>
                  <span className={styles.drawerSlotDetails}>
                    {slot._patientPhone}{slot.procedureName ? ` • ${slot.procedureName}` : ''}
                  </span>
                </>
              ) : (() => {
                const slotParsed = parseSlotDateTime(slot.date, slot.time);
                return (
                  <>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 500 }}>Pacientes compatíveis</span>
                    </div>
                    <span className={styles.drawerSlotTime}>
                      {slotParsed
                        ? `${slotParsed.day} de ${slotParsed.month} de ${slotParsed.year}${slotParsed.time ? ` às ${slotParsed.time}` : ''}`
                        : `${slot.date} às ${slot.time}`}
                    </span>
                    {slotParsed?.weekday && (
                      <span style={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 400 }}>
                        {slotParsed.weekday}
                      </span>
                    )}
                    <span className={styles.drawerSlotDetails}>
                      {slot.resourceName}{slot.procedureName ? ` • ${slot.procedureName}` : ''}{slot.roomName ? ` • ${slot.roomName}` : ''}
                    </span>
                  </>
                );
              })()}
            </div>
            <IconButton onClick={onClose} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        {/* Sort bar — only in default mode */}
        {!isPatientView && (
          <div className={styles.drawerSortBar}>
            <span className={styles.drawerSortLabel}>Ordenar por:</span>
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <Select
                value={sortBy}
                onChange={(e) => onSortChange?.(e.target.value)}
                sx={{ fontSize: '0.78rem' }}
              >
                <MenuItem value="compatibility">Melhor compatibilidade</MenuItem>
                <MenuItem value="waitingTime">Tempo de espera</MenuItem>
                <MenuItem value="priority">Prioridade</MenuItem>
              </Select>
            </FormControl>
          </div>
        )}

        {/* Content */}
        <div className={styles.drawerContent}>
          {compatiblePatients.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: '#94a3b8', fontSize: '0.85rem' }}>
              {isPatientView ? 'Nenhum horário disponível encontrado.' : 'Nenhum paciente compatível encontrado.'}
            </div>
          ) : (
            <>
              {/* Best opportunity featured card (patient view only) */}
              {isPatientView && bestSlot && (
                <BestOpportunityCard
                  slot={bestSlot}
                  offered={isBestOffered}
                  onOffer={() => onOfferSlot?.(bestSlot, slot)}
                  onSchedule={() => onSchedule?.(bestSlot, slot)}
                  onAccepted={() => onOfferAccepted?.(bestSlot, slot)}
                  onDenied={() => onOfferDenied?.(bestSlot, slot)}
                  onResend={() => onOfferSlot?.(bestSlot, slot)}
                />
              )}

              {/* Other slots / all patients */}
              {otherSlots.length > 0 && (
                <>
                  {isPatientView && (
                    <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 10 }}>
                      Outras oportunidades
                    </div>
                  )}
                  <div className={styles.drawerPatientsList}>
                    {otherSlots.map((item, index) => {
                      const parsed = isPatientView ? parseSlotDateTime(item.slotDate, item.slotTime) : null;
                      const compat = getCompatibilityLabel(item.compatibilityScore || 0);
                      // rank offset: best card is #1, so others start at #2
                      const rankDisplay = isPatientView ? index + 2 : index + 1;
                      const rankClass = isPatientView
                        ? (index === 0 ? styles.drawerPatientRank2 : index === 1 ? styles.drawerPatientRank3 : styles.drawerPatientRankDefault)
                        : getRankClass(index);

                      return (
                        <div key={item.id || index} className={styles.drawerPatientCard}>
                          <div className={`${styles.drawerPatientRank} ${rankClass}`}>
                            {rankDisplay}
                          </div>

                          <div className={styles.drawerPatientBody}>
                            {isPatientView ? (
                              /* Slot card */
                              <>
                                <div className={styles.drawerPatientHeader}>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, marginBottom: 4 }}>
                                      <CalendarMonthIcon sx={{ fontSize: '0.9rem', color: '#475569', flexShrink: 0, marginTop: '2px' }} />
                                      <div>
                                        <span style={{ fontSize: '0.88rem', fontWeight: 600, color: '#1e293b' }}>
                                          {parsed ? `${parsed.day} de ${parsed.month} de ${parsed.year}` : item.slotDate || '—'}
                                        </span>
                                        {parsed?.weekday && (
                                          <div style={{ fontSize: '0.7rem', color: '#94a3b8', marginTop: 1 }}>{parsed.weekday}</div>
                                        )}
                                      </div>
                                    </div>
                                    {parsed?.time && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                                        <ScheduleIcon sx={{ fontSize: '0.85rem', color: '#64748b', flexShrink: 0 }} />
                                        <span style={{ fontSize: '0.8rem', color: '#475569', fontWeight: 500 }}>{parsed.time}</span>
                                      </div>
                                    )}
                                    {item.resourceLabel && (
                                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <PersonOutlineIcon sx={{ fontSize: '0.85rem', color: '#94a3b8', flexShrink: 0 }} />
                                        <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>{item.resourceLabel}</span>
                                      </div>
                                    )}
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 2, flexShrink: 0 }}>
                                    <span style={{ fontSize: '0.7rem', fontWeight: 600, color: compat.color }}>{compat.label}</span>
                                  </div>
                                </div>
                              </>
                            ) : (
                              /* Patient card */
                              <>
                                <div className={styles.drawerPatientHeader}>
                                  <div>
                                    <div className={styles.drawerPatientName}>{item.name}</div>
                                    <div className={styles.drawerPatientPhoneText}>{item.phone}</div>
                                  </div>
                                  <span className={getPriorityClass(item.priority)}>
                                    {PRIORITY_LABELS[item.priority] || 'Média'}
                                  </span>
                                </div>
                                <div className={styles.drawerPatientGrid}>
                                  <div className={styles.drawerPatientField}>
                                    <span className={styles.drawerFieldLabel}>Tempo de espera</span>
                                    <span className={styles.drawerFieldValue}>{item.waitingDays || 0} dias</span>
                                  </div>
                                  <div className={styles.drawerPatientField}>
                                    <span className={styles.drawerFieldLabel}>Prefere</span>
                                    <span className={styles.drawerFieldValue}>{item.preferredTimesLabel || '—'}</span>
                                  </div>
                                  <div className={styles.drawerPatientField}>
                                    <span className={styles.drawerFieldLabel}>Último contato</span>
                                    <span className={styles.drawerFieldValue}>{item.lastContact || '—'}</span>
                                  </div>
                                  <div className={styles.drawerPatientField}>
                                    <span className={styles.drawerFieldLabel}>Compatibilidade</span>
                                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: compat.color }}>{compat.label}</span>
                                  </div>
                                </div>
                              </>
                            )}

                            {/* Actions */}
                            <div className={styles.drawerPatientActions}>
                              <Button
                                variant="contained"
                                size="small"
                                className={styles.offerBtn}
                                onClick={() => onOfferSlot?.(item, slot)}
                              >
                                Oferecer horário
                              </Button>
                              <Button
                                variant="outlined"
                                size="small"
                                className={styles.scheduleBtn}
                                onClick={() => onSchedule?.(item, slot)}
                              >
                                Agendar
                              </Button>
                              <IconButton size="small" style={{ color: '#94a3b8' }}>
                                <MoreHorizIcon fontSize="small" />
                              </IconButton>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {totalCount > (isPatientView ? otherSlots.length + 1 : otherSlots.length) && (
          <div className={styles.drawerFooter}>
            <span className={styles.viewAllLink}>
              Ver todos os {totalCount} {isPatientView ? 'horários' : 'pacientes'}
            </span>
          </div>
        )}
      </div>
    </Drawer>
  );
}

export default CompatiblePatientsDrawer;
